/**
 * Turbine efficiency (HYDRAULICS §6.3).
 *
 * The interface is `efficiency(q, h)` with q = Q/Q_rated and h = H_net/H_rated. Sites supply a
 * model, not a formula — a Francis parabolic bowl for Kettle Narrows, a flat-topped Kaplan curve
 * for Lower Sable. Adding a tabulated chart means adding a variant here, never a site branch.
 */

import { clamp } from './constants';
import type { EfficiencyModel } from './types';

/**
 * Kettle Narrows (parabolic):
 *   η = clamp(0.94 − 0.55·(q − 0.85)² − 0.90·(h − 1)², 0.60, 0.94)
 * Best efficiency at q = 0.85 — a Francis runner peaking below rated flow is ordinary, and it is
 * why the rated point makes 294.09 MW rather than the 300 MW nameplate (DECISIONS D4).
 *
 * Lower Sable (flat-top): η = η_max across q ∈ [0.5, 1.0], falling to 0.80 at q = 0.3. Kaplan
 * blades adjust with load, so the curve is a plateau rather than a bowl.
 */
export function efficiency(model: EfficiencyModel, q: number, h: number): number {
  if (model.kind === 'parabolic') {
    const dq = q - model.qBep;
    const dh = h - 1;
    return clamp(model.etaPeak - model.kq * dq * dq - model.kh * dh * dh, model.etaMin, model.etaPeak);
  }
  const dh = h - 1;
  const headLoss = model.kh * dh * dh;
  let base: number;
  if (q >= model.flatLo && q <= model.flatHi) {
    base = model.etaMax;
  } else if (q < model.flatLo) {
    // Fit a parabola through (flatLo, etaMax) and (etaLowQ, etaLowValue) with zero slope at flatLo.
    const span = model.flatLo - model.etaLowQ;
    const k = span > 0 ? (model.etaMax - model.etaLowValue) / (span * span) : 0;
    const d = model.flatLo - q;
    base = model.etaMax - k * d * d;
  } else {
    // Above the plateau the curve rolls off at the same curvature as the lower skirt.
    const span = model.flatLo - model.etaLowQ;
    const k = span > 0 ? (model.etaMax - model.etaLowValue) / (span * span) : 0;
    const d = q - model.flatHi;
    base = model.etaMax - k * d * d;
  }
  return clamp(base - headLoss, model.etaMin, model.etaMax);
}

/** Best-efficiency flow ratio, for the hill-chart overlay and the efficiency benchmark. */
export function bestEfficiencyQ(model: EfficiencyModel): number {
  return model.kind === 'parabolic' ? model.qBep : (model.flatLo + model.flatHi) / 2;
}

export function peakEfficiency(model: EfficiencyModel): number {
  return model.kind === 'parabolic' ? model.etaPeak : model.etaMax;
}
